/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/**
 * ------------------------------------------------------------------------------------------------------------
 * MOBILE NAVIGATION DRILL-DOWN
 * ------------------------------------------------------------------------------------------------------------
 *
 * The mobile menu (snippets/mobile-menu.liquid) renders one panel per top-level item that has children, stacked
 * on top of one another. Tapping an item slides the drawer over to that item's panel; the back button returns to
 * the root. The sliding itself is CSS -- all this does is move the .is-active class.
 *
 * Delegated from the document so it survives the drawer being moved: <mobile-navigation> carries the
 * "append-body" attribute, so the theme relocates the element to the end of <body> after it upgrades.
 */
(function () {
  function panelsOf(drawer) {
    return drawer.querySelectorAll('[data-mobile-nav-panel]');
  }

  function show(drawer, panel) {
    var root = drawer.querySelector('[data-mobile-nav-panel="root"]');

    panelsOf(drawer).forEach(function (candidate) {
      var active = candidate === panel;

      candidate.classList.toggle('is-active', active);

      // Only the root ever sits behind: this is a two-level menu, so the panel
      // being drilled down from is always the root.
      candidate.classList.toggle('is-behind', !active && candidate === root);

      // Inactive panels are visibility: hidden, which already takes them out of
      // the tab order. inert additionally stops a screen reader reaching the
      // root while it is parked behind an open submenu.
      candidate.toggleAttribute('inert', !active);
    });

    // Each panel scrolls independently, so a panel opened after another was
    // scrolled would otherwise inherit nothing and simply appear mid-list.
    var body = panel.querySelector('.mobile-nav-panel__body');

    if (body) {
      body.scrollTop = 0;
    }
  }

  function reset(drawer) {
    var root = drawer.querySelector('[data-mobile-nav-panel="root"]');

    if (root) {
      show(drawer, root);
    }
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-mobile-nav-open]');
    var back = event.target.closest('[data-mobile-nav-back]');

    if (!opener && !back) {
      return;
    }

    var drawer = (opener || back).closest('.mobile-nav-drawer');

    if (!drawer) {
      return;
    }

    if (opener) {
      var panel = drawer.querySelector('#' + opener.getAttribute('data-mobile-nav-open'));

      if (panel) {
        show(drawer, panel);
      }
    } else {
      reset(drawer);
    }
  });

  /**
   * Return to the root once the drawer is closed, so it does not re-open on a submenu. Watching the "open"
   * attribute rather than listening for a close event catches every route out of the drawer -- the close button,
   * the overlay, and the Escape key alike.
   */
  function observe(drawer) {
    var observer = new MutationObserver(function () {
      if (!drawer.hasAttribute('open')) {
        reset(drawer);
      }
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  document.querySelectorAll('.mobile-nav-drawer').forEach(observe);
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * QUICK LINKS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Three independent pieces of behaviour for the "Quick links" section: the tab row, the clock, and the current
 * conditions readout. Each is set up on its own so that a failure in one -- an unreachable weather service, most
 * likely -- leaves the other two working.
 *
 * See sections/quick-links.liquid for the markup and the "Quick links" block in custom.css for the styling.
 */

/**
 * TABS
 *
 * Every panel is already in the DOM; selecting a tab is only a matter of moving the `hidden` attribute. Follows the
 * WAI-ARIA tabs pattern with automatic activation: the arrow keys both move focus and change the selection, and
 * only the selected tab is in the tab order (a "roving tabindex"), so Tab steps past the row into the panel.
 */
(function () {
  'use strict';

  function select(tab) {
    var tablist = tab.closest('[role="tablist"]');

    if (!tablist) {
      return;
    }

    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('.quick-links__tab[role="tab"]'));

    tabs.forEach(function (candidate) {
      var isSelected = candidate === tab;
      var panel = document.getElementById(candidate.getAttribute('aria-controls'));

      candidate.classList.toggle('is-active', isSelected);
      candidate.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      candidate.setAttribute('tabindex', isSelected ? '0' : '-1');

      if (panel) {
        panel.classList.toggle('is-active', isSelected);
        panel.hidden = !isSelected;
      }
    });
  }

  document.addEventListener('click', function (event) {
    var tab = event.target.closest('.quick-links__tab[role="tab"]');

    if (tab) {
      select(tab);
    }
  });

  document.addEventListener('keydown', function (event) {
    var tab = event.target.closest('.quick-links__tab[role="tab"]');

    if (!tab) {
      return;
    }

    var tabs = Array.prototype.slice.call(tab.closest('[role="tablist"]').querySelectorAll('.quick-links__tab[role="tab"]'));
    var index = tabs.indexOf(tab);
    var next = null;

    // Arrow keys are physical, so the two horizontal ones swap over in a right-to-left locale.
    var isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + (isRtl ? -1 : 1) + tabs.length) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index + (isRtl ? 1 : -1) + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    select(next);
    next.focus();
  });
}());

/**
 * CLOCK
 *
 * Ticks in the timezone set on the section rather than the visitor's own, which is the whole point: someone
 * checking a flight from another timezone needs the time at the airport. Intl does the conversion, so no offset
 * arithmetic and no daylight-saving table.
 *
 * The section prints a first value server-side, in the store's timezone, purely so the line does not appear empty;
 * the first tick below replaces it within a frame.
 */
(function () {
  'use strict';

  function formatterFor(element) {
    var options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: element.getAttribute('data-hour-cycle') !== '24'
    };

    var timezone = element.getAttribute('data-timezone');

    if (timezone) {
      options.timeZone = timezone;
    }

    try {
      return new Intl.DateTimeFormat(undefined, options);
    } catch (error) {
      // An unrecognised timezone name throws. Fall back to the visitor's own rather than dropping the clock.
      delete options.timeZone;
      return new Intl.DateTimeFormat(undefined, options);
    }
  }

  function start(element) {
    var formatter = formatterFor(element);

    // Rendered by Liquid, so there is no markup to build here -- only two text nodes to write. The fallbacks keep
    // the clock working if the spans are ever taken back out of the section.
    var valueNode = element.querySelector('[data-quick-links-clock-value]') || element;
    var meridiemNode = element.querySelector('[data-quick-links-clock-meridiem]');

    function tick() {
      // The theme editor replaces the whole section on every edit. Ending the chain when the element is no longer
      // in the document stops each edit from leaving a timer running against a detached node.
      if (!element.isConnected) {
        return;
      }

      var now = new Date();
      var time = '';
      var meridiem = '';

      // formatToParts rather than splitting the formatted string: it is Intl that knows which run of characters is
      // the meridiem, and that varies by locale -- some put it first, and the separator is not always a space.
      formatter.formatToParts(now).forEach(function (part) {
        if (part.type === 'dayPeriod') {
          meridiem = part.value;
        } else {
          time += part.value;
        }
      });

      // The separator Intl puts between the two -- a narrow no-break space in recent versions -- is left behind in
      // `time` once the meridiem is lifted out, and the design sets the pair solid anyway.
      time = time.replace(/\s+/g, '');

      if (meridiemNode) {
        valueNode.textContent = time;
        meridiemNode.textContent = meridiem;
      } else {
        valueNode.textContent = time + meridiem;
      }

      // Re-aimed at the next minute boundary on every tick instead of a 60s interval, so the clock cannot drift
      // and cannot sit a minute behind after the tab has been backgrounded.
      setTimeout(tick, 60000 - (now.getSeconds() * 1000 + now.getMilliseconds()));
    }

    tick();
  }

  function init(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-quick-links-clock]'), start);
  }

  init(document);

  // Re-run for a section the theme editor has just re-rendered, so the clock does not sit frozen on the value
  // Liquid printed while a merchant is editing.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
}());

/**
 * CURRENT CONDITIONS
 *
 * Two sources, both free and neither needing an account or an API key:
 *
 *   - api.weather.gov (National Weather Service). Public domain, US only, and reports the actual observation from
 *     the airport's own station, which is the closest equivalent to what the current site shows.
 *   - api.open-meteo.com. Worldwide, free for non-commercial use, but a model reading for a coordinate rather than
 *     a station report.
 *
 * The reading is cached in sessionStorage for an hour, matching the cadence of the routine observation a station
 * files, so every page view within a session after the first costs no request at all.
 *
 * Worth knowing if this is ever revisited: the hour is the *routine* cadence only. A station also files an
 * off-cycle "special" report when conditions change sharply -- which is exactly the weather an airport cares
 * about -- and Open-Meteo re-runs every 15 minutes. A shorter window is what would pick either of those up.
 *
 * The element stays hidden until a reading arrives, so a service that is down or slow leaves the time on its own
 * rather than showing a stale or empty temperature.
 */
(function () {
  'use strict';

  if (typeof fetch !== 'function') {
    return;
  }

  var CACHE_TTL = 60 * 60 * 1000;

  // WMO 4677 weather codes, as returned by Open-Meteo. Grouped rather than exhaustive: the readout is one word
  // wide, so "Drizzle" covers all three intensities of it.
  var WMO_DESCRIPTIONS = {
    0: 'Sunny', 1: 'Mostly sunny', 2: 'Partly cloudy', 3: 'Cloudy',
    45: 'Fog', 48: 'Fog',
    51: 'Drizzle', 53: 'Drizzle', 55: 'Drizzle', 56: 'Freezing drizzle', 57: 'Freezing drizzle',
    61: 'Light rain', 63: 'Rain', 65: 'Heavy rain', 66: 'Freezing rain', 67: 'Freezing rain',
    71: 'Light snow', 73: 'Snow', 75: 'Heavy snow', 77: 'Snow grains',
    80: 'Showers', 81: 'Showers', 82: 'Heavy showers', 85: 'Snow showers', 86: 'Snow showers',
    95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Thunderstorm'
  };

  // A cloudless sky is not "sunny" after dark, and the first three codes are the only ones the distinction
  // applies to. Open-Meteo reports which side of it the reading falls on as `is_day`.
  var WMO_NIGHT_DESCRIPTIONS = { 0: 'Clear', 1: 'Mostly clear', 2: 'Partly cloudy' };

  function endpoint(element) {
    var source = element.getAttribute('data-source');

    if (source === 'open_meteo') {
      return 'https://api.open-meteo.com/v1/forecast' +
        '?latitude=' + encodeURIComponent(element.getAttribute('data-latitude')) +
        '&longitude=' + encodeURIComponent(element.getAttribute('data-longitude')) +
        '&current=temperature_2m,weather_code,is_day' +
        '&temperature_unit=' + encodeURIComponent(element.getAttribute('data-unit'));
    }

    return 'https://api.weather.gov/stations/' +
      encodeURIComponent((element.getAttribute('data-station') || '').toUpperCase()) +
      '/observations/latest';
  }

  /**
   * Both services are normalised to { temperature, description } here, so nothing downstream has to know which one
   * answered. Returns null when the reading is unusable -- an out-of-service station reports a null temperature.
   */
  function read(element, payload) {
    var wantsFahrenheit = element.getAttribute('data-unit') !== 'celsius';

    if (element.getAttribute('data-source') === 'open_meteo') {
      var current = payload.current || {};

      if (typeof current.temperature_2m !== 'number') {
        return null;
      }

      var descriptions = current.is_day === 0 ? WMO_NIGHT_DESCRIPTIONS : WMO_DESCRIPTIONS;

      // Open-Meteo converts the temperature itself, from the unit asked for in the query string.
      return {
        temperature: current.temperature_2m,
        description: descriptions[current.weather_code] || WMO_DESCRIPTIONS[current.weather_code] || ''
      };
    }

    var properties = payload.properties || {};
    var celsius = properties.temperature && properties.temperature.value;

    if (typeof celsius !== 'number') {
      return null;
    }

    var description = properties.textDescription || '';

    // The service reports a cloudless sky as "Clear" around the clock. That reads oddly next to an afternoon time,
    // so it becomes "Sunny" while the station's own icon says it is daytime.
    if (/^(clear|fair)$/i.test(description) && /\/day\//.test(properties.icon || '')) {
      description = 'Sunny';
    }

    return {
      temperature: wantsFahrenheit ? celsius * 9 / 5 + 32 : celsius,
      description: description
    };
  }

  /**
   * Temperature and condition go into separate spans so they can be set differently -- see the "Quick links" block
   * in custom.css. Built with createElement and textContent rather than innerHTML: the description is a string
   * from a third party, and this way it can never be parsed as markup however odd it turns out to be.
   */
  function render(element, reading) {
    if (!reading) {
      return;
    }

    var value = document.createElement('span');

    value.className = 'quick-links__weather-value';
    value.textContent = Math.round(reading.temperature) + '°';

    element.textContent = '';
    element.appendChild(value);

    if (reading.description) {
      var condition = document.createElement('span');

      condition.className = 'quick-links__weather-condition';
      condition.textContent = reading.description;

      // A real space rather than a margin, so the pair still reads as one run of text when it wraps.
      element.appendChild(document.createTextNode(' '));
      element.appendChild(condition);
    }

    element.hidden = false;
  }

  /**
   * sessionStorage throws outright in a few situations -- Safari's private mode historically, and any embedding
   * that blocks storage -- so every access is guarded and a failure simply means the request is made again.
   */
  function cached(key) {
    try {
      var entry = JSON.parse(window.sessionStorage.getItem(key));

      if (entry && Date.now() - entry.time < CACHE_TTL) {
        return entry.reading;
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  function cache(key, reading) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), reading: reading }));
    } catch (error) {
      // Full or unavailable storage is not worth reporting: the reading still rendered.
    }
  }

  function load(element) {
    var url = endpoint(element);

    // Keyed by unit as well as endpoint: the National Weather Service always answers in Celsius and the
    // conversion happens below, so the unit is not part of its URL but is part of what got cached.
    var key = 'quick-links-weather:' + element.getAttribute('data-unit') + ':' + url;
    var hit = cached(key);

    if (hit) {
      render(element, hit);
      return;
    }

    fetch(url, { headers: { Accept: 'application/geo+json, application/json' } })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Weather request failed with status ' + response.status);
        }

        return response.json();
      })
      .then(function (payload) {
        var reading = read(element, payload);

        if (reading) {
          cache(key, reading);
          render(element, reading);
        }
      })
      .catch(function () {
        // Nothing to do: the element was rendered hidden and stays that way.
      });
  }

  function init(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-quick-links-weather]'), load);
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * INTERACTIVE GALLERY
 * ------------------------------------------------------------------------------------------------------------
 *
 * The strip of image tiles in the "Interactive gallery" section. One tile is open at a time; clicking a closed one
 * opens it and closes whichever was open, and the caption underneath follows.
 *
 * The expand itself is NOT animated here. The tiles are flex items whose basis and grow factor change with the
 * .is-active class, and both of those properties interpolate, so the browser animates the whole reflow from a CSS
 * transition. All this does is move a class and an attribute.
 *
 * Every caption is already in the DOM, under the tile it belongs to, so nothing is copied into it at runtime --
 * selecting is only a matter of moving the `hidden` attribute. Living inside the tile is also what puts a caption
 * under the open picture rather than at the start of the row, with nothing here having to measure anything.
 *
 * The arrows do two different jobs by width: side by side they scroll the strip and leave the selection alone,
 * stacked they step the selection, since only one tile is on screen there and nothing overflows to scroll.
 *
 * Stacked, every tile is a full-width pane and the strip slides between them. That slide is a CSS transition too:
 * all this writes is the open index onto the track, which the transform reads.
 *
 * See sections/tile-gallery.liquid for the markup and the "Interactive gallery" block in custom.css.
 */
(function () {
  'use strict';

  /**
   * The wrappers, not the buttons: the wrapper is the flex item that widens and the box the caption sits in, so
   * it is what .is-active belongs on. The button inside it is only the picture.
   */
  function tilesOf(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('[data-tile-gallery-item]'));
  }

  function buttonOf(tile) {
    return tile.querySelector('[data-tile-gallery-tile]');
  }

  /**
   * One per tile, in the same order. CSS already hides the caption row of every closed tile; moving `hidden` as
   * well is what keeps a closed caption out of the accessibility tree, and what the no-JS state relies on.
   */
  function captionsOf(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('[data-tile-gallery-caption]'));
  }

  function trackOf(gallery) {
    return gallery.querySelector('[data-tile-gallery-track]');
  }

  // Below this width the tiles stack one at a time, which is what the arrows and the swipe both key off.
  function isStacked() {
    return window.matchMedia('(max-width: 740.98px)').matches;
  }

  function isRtl() {
    return document.documentElement.getAttribute('dir') === 'rtl';
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Stacked, the pane is reached by translating the track, so there is nothing here to scroll.
  function showIndex(gallery, index) {
    var track = trackOf(gallery);

    if (track) {
      track.style.setProperty('--tile-gallery-index', index);
    }
  }

  function scrollIntoView(gallery, tile) {
    var track = trackOf(gallery);

    if (isStacked() || !track || track.scrollWidth <= track.clientWidth) {
      return;
    }

    // `block: 'nearest'` so that bringing a tile into view sideways never also scrolls the page vertically.
    tile.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
  }

  /**
   * Side by side, the arrows scroll the strip rather than moving the selection: what is off screen is brought into
   * view and whichever tile is open stays open.
   */
  function scrollTrack(gallery, back) {
    var track = trackOf(gallery);

    if (!track) {
      return;
    }

    // Just under a full width, so the tile at the edge stays partly visible and the eye keeps its place.
    var page = Math.max(track.clientWidth * 0.8, 120);

    // scrollLeft runs negative in a right-to-left locale, so travelling forwards is a decrease there.
    var direction = (back ? -1 : 1) * (isRtl() ? -1 : 1);

    track.scrollBy({ left: page * direction, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  function select(gallery, index) {
    var tiles = tilesOf(gallery);

    if (!tiles.length) {
      return;
    }

    // Clamped rather than wrapped: the arrows are hidden at the ends, so arriving out of range means a stale index
    // rather than an intentional wrap.
    index = Math.max(0, Math.min(index, tiles.length - 1));

    tiles.forEach(function (tile, i) {
      var isOpen = i === index;
      var button = buttonOf(tile);

      tile.classList.toggle('is-active', isOpen);

      if (button) {
        button.setAttribute('aria-current', isOpen ? 'true' : 'false');
      }
    });

    captionsOf(gallery).forEach(function (caption, i) {
      caption.hidden = i !== index;
    });

    showIndex(gallery, index);
    updateArrows(gallery);

    var tile = tiles[index];

    // Nothing widens and nothing scrolls in the stacked layout, so neither pass below has anything to do there.
    if (isStacked()) {
      return;
    }

    scrollIntoView(gallery, tile);

    // Again once the tile has finished widening: the first call measures a tile that is still mid-transition, so
    // on a long strip it can stop short of where the tile ends up.
    tile.addEventListener('transitionend', function once(event) {
      if (event.propertyName !== 'flex-basis') {
        return;
      }

      tile.removeEventListener('transitionend', once);
      scrollIntoView(gallery, tile);
    });
  }

  /**
   * Stacked, the arrows step the selection and are hidden at the two ends of the run.
   *
   * Side by side they scroll the strip instead, so they are hidden when there is nothing left to reach in that
   * direction -- and both go when everything already fits, where a pair of arrows would have nothing to do.
   */
  function updateArrows(gallery) {
    var track = trackOf(gallery);
    var prev = gallery.querySelector('[data-tile-gallery-prev]');
    var next = gallery.querySelector('[data-tile-gallery-next]');

    if (isStacked()) {
      var index = indexOf(gallery);
      var count = tilesOf(gallery).length;

      if (prev) {
        prev.hidden = index <= 0;
      }

      if (next) {
        next.hidden = index >= count - 1;
      }

      return;
    }

    // scrollLeft is negative in a right-to-left locale, so the distance travelled is its magnitude either way.
    var travel = track ? track.scrollWidth - track.clientWidth : 0;
    var position = track ? Math.abs(track.scrollLeft) : 0;

    if (prev) {
      prev.hidden = travel <= 1 || position <= 1;
    }

    if (next) {
      next.hidden = travel <= 1 || position >= travel - 1;
    }
  }

  function indexOf(gallery) {
    var tiles = tilesOf(gallery);

    for (var i = 0; i < tiles.length; i++) {
      if (tiles[i].classList.contains('is-active')) {
        return i;
      }
    }

    return 0;
  }

  document.addEventListener('click', function (event) {
    var tile = event.target.closest('[data-tile-gallery-tile]');
    var step = event.target.closest('[data-tile-gallery-prev], [data-tile-gallery-next]');

    if (!tile && !step) {
      return;
    }

    var gallery = (tile || step).closest('[data-tile-gallery]');

    if (!gallery) {
      return;
    }

    if (tile) {
      select(gallery, parseInt(tile.getAttribute('data-index'), 10) || 0);
    } else if (isStacked()) {
      select(gallery, indexOf(gallery) + (step.hasAttribute('data-tile-gallery-prev') ? -1 : 1));
    } else {
      scrollTrack(gallery, step.hasAttribute('data-tile-gallery-prev'));
    }
  });

  /**
   * Stacked only: a horizontal swipe steps the selection. Side by side the strip already scrolls under the finger,
   * and a gesture there must not move the selection as well.
   *
   * The tiles are buttons, so a swipe that ends on one would otherwise be followed by a click that selects it back.
   */
  var swipe = null;
  var swallowClick = false;

  document.addEventListener('touchstart', function (event) {
    var gallery = event.target.closest && event.target.closest('[data-tile-gallery]');

    swipe = null;
    swallowClick = false;

    if (gallery && isStacked() && event.touches.length === 1) {
      swipe = { gallery: gallery, x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
  }, { passive: true });

  document.addEventListener('touchend', function (event) {
    if (!swipe || !event.changedTouches.length) {
      return;
    }

    var gallery = swipe.gallery;
    var dx = event.changedTouches[0].clientX - swipe.x;
    var dy = event.changedTouches[0].clientY - swipe.y;

    swipe = null;

    // Has to read clearly as sideways, so a diagonal flick while scrolling the page is left alone.
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) {
      return;
    }

    var forwards = isRtl() ? dx > 0 : dx < 0;
    var index = indexOf(gallery);
    var next = index + (forwards ? 1 : -1);

    if (next < 0 || next > tilesOf(gallery).length - 1) {
      return;
    }

    swallowClick = true;
    select(gallery, next);
  }, { passive: true });

  // Capture phase, so the click never reaches the tile handler above.
  document.addEventListener('click', function (event) {
    if (!swallowClick) {
      return;
    }

    swallowClick = false;
    event.stopPropagation();
    event.preventDefault();
  }, true);

  /**
   * Arrow keys step through the strip while a tile has focus, which is what a visitor tabbing into it will reach
   * for. Focus follows the selection so the newly opened tile is the one the next key press acts on.
   */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    var tile = event.target.closest('[data-tile-gallery-tile]');
    var gallery = tile && tile.closest('[data-tile-gallery]');

    if (!gallery) {
      return;
    }

    // Arrow keys are physical, so the two horizontal ones swap over in a right-to-left locale.
    var isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    var forwards = event.key === 'ArrowRight' ? !isRtl : isRtl;
    var next = indexOf(gallery) + (forwards ? 1 : -1);
    var tiles = tilesOf(gallery);

    if (next < 0 || next > tiles.length - 1) {
      return;
    }

    event.preventDefault();
    select(gallery, next);

    var button = buttonOf(tiles[next]);

    if (button) {
      button.focus();
    }
  });

  // Whether the strip overflows depends on the width it has, so the arrows are re-decided when that changes --
  // including the crossing of the stacked breakpoint, where they become the only way through.
  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-tile-gallery]').forEach(function (gallery) {
      updateArrows(gallery);
    });
  });

  function init(root) {
    root.querySelectorAll('[data-tile-gallery]').forEach(function (gallery) {
      var track = trackOf(gallery);

      if (track) {
        // Dragging and trackpad scrolling move the strip without going through select(), so the arrows follow the
        // scroll position itself. Coalesced to one reading a frame.
        var pending = false;

        track.addEventListener('scroll', function () {
          if (pending) {
            return;
          }

          pending = true;

          window.requestAnimationFrame(function () {
            pending = false;
            updateArrows(gallery);
          });
        }, { passive: true });
      }

      showIndex(gallery, indexOf(gallery));
      updateArrows(gallery);
    });
  }

  init(document);

  // The theme editor replaces the whole section on every edit, so a re-rendered strip has to be measured again.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * BACKGROUND BANDS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Sizes the bands in the "Background bands" section. The section sits above the run of sections it paints behind,
 * and each band block covers the next N of them, in order -- so a band starts where the one before it stopped.
 *
 * "Nudge start" moves a boundary off that join, for the case where the background changes part-way through a
 * section. Only the start moves: the band above is measured from it, so the two can never gap or double up.
 *
 * Parallax is a transform on the inner layer only, so nothing here ever changes layout.
 */
(function () {
  var MOBILE = window.matchMedia('(max-width: 740.98px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  var groups = [];
  var bands = [];
  var frame = null;
  var scrolling = false;
  var observer = null;

  function root() {
    return document.getElementById('main');
  }

  function collect() {
    var main = root();

    groups = main ? Array.prototype.slice.call(main.querySelectorAll('.background-bands')) : [];
    bands = groups.reduce(function (list, group) {
      return list.concat(Array.prototype.slice.call(group.querySelectorAll('.background-band')));
    }, []);
  }

  // The sections a group paints behind: everything after its own marker, in order.
  function sectionsAfter(group) {
    var node = group.closest('.shopify-section');
    var list = [];

    node = node && node.nextElementSibling;

    while (node) {
      if (node.classList.contains('shopify-section')) {
        list.push(node);
      }

      node = node.nextElementSibling;
    }

    return list;
  }

  function number(element, attribute, fallback) {
    var value = parseFloat(element.getAttribute(attribute));

    return isNaN(value) ? fallback : value;
  }

  // The diagonal is quoted at 1440px wide and scales with the viewport, so the band above has to reach that far
  // too. One extra pixel keeps a rounding error from showing as a hairline at the deepest corner.
  function cutDepth(band, width) {
    var quoted = number(band, MOBILE.matches ? 'data-diagonal-mobile' : 'data-diagonal', 0);

    return quoted ? Math.ceil(quoted * width / 1440) + 1 : 0;
  }

  // `sizes` describes a slot's WIDTH, but these images are object-fit: cover in a box whose height is content
  // driven -- so a band taller than its artwork's aspect ratio allows makes the browser upscale to fill
  // vertically. Restating `sizes` as the width the image actually has to be lets it pick a large enough candidate.
  function retarget(band, height, width) {
    var img = band.querySelector('img');
    var aspect = number(band, MOBILE.matches ? 'data-aspect-mobile' : 'data-aspect', 0);

    if (!img || !aspect) {
      return;
    }

    var layer = height * (1 + number(band, 'data-parallax', 0));
    var value = Math.ceil(Math.max(width, layer * aspect)) + 'px';

    if (img.getAttribute('sizes') !== value) {
      img.setAttribute('sizes', value);
    }

    var source = band.querySelector('source');

    if (source && source.getAttribute('sizes') !== value) {
      source.setAttribute('sizes', value);
    }
  }

  function size(group, mainTop, mainHeight, mainWidth) {
    var own = Array.prototype.slice.call(group.querySelectorAll('.background-band'));

    if (!own.length) {
      return;
    }

    var sections = sectionsAfter(group);
    var key = MOBILE.matches ? 'data-offset-mobile' : 'data-offset';

    // Every read is taken before any write, so the pass costs one layout rather than one per band.
    var starts = [];
    var cursor = 0;

    own.forEach(function (band, index) {
      var edge;

      if (index === 0) {
        edge = sections.length ? sections[0].getBoundingClientRect().top + window.pageYOffset - mainTop : 0;
      } else {
        // The bottom of the last section the bands above this one covered between them.
        var previous = sections[cursor - 1];
        edge = previous ? previous.getBoundingClientRect().bottom + window.pageYOffset - mainTop : mainHeight;
      }

      starts.push(edge + number(band, key, 0));
      cursor += Math.max(1, number(band, 'data-sections', 1));
    });

    // Where the last band stops. Falls back to the end of the page when the counts reach past the last section.
    var final = sections[cursor - 1];
    var tail = final ? final.getBoundingClientRect().bottom + window.pageYOffset - mainTop : mainHeight;

    own.forEach(function (band, index) {
      var next = own[index + 1];

      // A band runs on underneath the next one, far enough for that band's fade to have something to blend
      // into and for any transparency in its artwork to show blue rather than bare page.
      var under = next ? Math.max(number(next, 'data-overlap', 0), number(next, 'data-fade', 0),
                                  cutDepth(next, mainWidth)) : 0;
      var end = next ? starts[index + 1] + under : tail;

      var height = Math.max(0, end - starts[index]);

      band.style.top = starts[index] + 'px';
      band.style.height = height + 'px';

      retarget(band, height, mainWidth);
    });
  }

  function measure() {
    var main = root();

    if (!main || !groups.length) {
      return;
    }

    var mainTop = main.getBoundingClientRect().top + window.pageYOffset;
    var mainHeight = main.offsetHeight;
    var mainWidth = main.offsetWidth;

    groups.forEach(function (group) {
      size(group, mainTop, mainHeight, mainWidth);
    });

    draw();
  }

  function draw() {
    if (REDUCED.matches) {
      return;
    }

    var viewport = window.innerHeight;

    bands.forEach(function (band) {
      var strength = number(band, 'data-parallax', 0);
      var layer = band.firstElementChild;

      if (!strength || !layer) {
        return;
      }

      var rect = band.getBoundingClientRect();

      if (rect.bottom < 0 || rect.top > viewport) {
        return;
      }

      // 0 as the band enters the bottom of the viewport, 1 as it leaves the top. The layer overhangs the band by
      // half the strength at each end, which is exactly the travel this produces.
      var progress = (viewport - rect.top) / (viewport + rect.height);

      layer.style.transform = 'translate3d(0, ' + ((0.5 - progress) * rect.height * strength).toFixed(2) + 'px, 0)';
    });
  }

  function schedule() {
    if (frame) {
      return;
    }

    frame = requestAnimationFrame(function () {
      frame = null;
      draw();
    });
  }

  // Scroll and the observer are the two that fire constantly, so they are only bound while a band is actually
  // present -- and scroll only while one of them drifts. Every other page pays nothing.
  function listen() {
    var present = groups.length > 0;
    var drifts = present && bands.some(function (band) {
      return number(band, 'data-parallax', 0) > 0;
    });

    if (present && !observer && 'ResizeObserver' in window && root()) {
      observer = new ResizeObserver(measure);
      observer.observe(root());
    } else if (!present && observer) {
      observer.disconnect();
      observer = null;
    }

    if (drifts && !scrolling) {
      window.addEventListener('scroll', schedule, { passive: true });
      scrolling = true;
    } else if (!drifts && scrolling) {
      window.removeEventListener('scroll', schedule);
      scrolling = false;
    }
  }

  function refresh() {
    collect();
    measure();
    listen();
  }

  refresh();

  window.addEventListener('resize', measure, { passive: true });

  // Images arriving after parse change every section height below them.
  window.addEventListener('load', measure);

  // The theme editor re-renders sections in place, which moves every band below the one that changed.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (name) {
    document.addEventListener(name, refresh);
  });
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * ACCORDION LIST
 * ------------------------------------------------------------------------------------------------------------
 *
 * The slide for the "Accordion list" section. <details> shows and hides its content instantly -- there is no
 * height to transition, because the content simply is not rendered while closed -- so the panel's own height is
 * animated instead, from the bar alone to the bar plus its content.
 *
 * Everything here is enhancement. With JavaScript off the panels still open and close; they just jump.
 *
 * COST OFF THE PAGES THAT USE IT. The section is on a handful of pages, so nothing is bound until one is found:
 * a storefront page without an accordion pays one querySelector at parse and registers no listeners at all. The
 * two theme editor events are only subscribed to in the editor, where Shopify.designMode is set.
 *
 * EXCLUSIVITY. The section can render name="..." on each <details>, which makes the browser close the others by
 * itself. That is instant and cannot be intercepted -- the sibling is closed the moment `open` is set on the one
 * being opened, which would kill its slide half-way. So the attribute is lifted off here and the same rule is
 * applied below, where it can be animated. Removing it is safe precisely because it only happens when this file
 * runs, which is the only time there is an animation to protect.
 *
 * See sections/accordion-list.liquid for the markup and the "Accordion list" block in custom.css.
 */
(function () {
  'use strict';

  var EASING = 'cubic-bezier(.4, 0, .2, 1)';
  var bound = false;

  function panelsIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.accordion-list__panel'));
  }

  function barOf(panel) {
    return panel.querySelector('.accordion-list__bar');
  }

  function duration(panel) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 0;
    }

    var section = panel.closest('[data-accordion-list]');
    var value = section ? parseInt(section.getAttribute('data-duration'), 10) : NaN;

    return isNaN(value) ? 320 : value;
  }

  // Web Animations rather than a CSS transition: the panel has no height to transition from, so both ends have to
  // be measured and handed over as explicit pixel values.
  function slide(panel, to, done) {
    var from = panel.getBoundingClientRect().height;
    var ms = duration(panel);

    if (panel.slideAnimation) {
      panel.slideAnimation.cancel();
    }

    // Nothing to move, no animation asked for, or no support: settle straight away.
    if (from === to || !ms || !panel.animate) {
      if (done) done();
      return;
    }

    // Clipped only while it moves, so the bar's focus ring is never cut off at rest.
    panel.style.overflow = 'hidden';

    var animation = panel.animate(
      { height: [from + 'px', to + 'px'] },
      { duration: ms, easing: EASING }
    );

    panel.slideAnimation = animation;

    // done() runs first: the animation holds no fill, so the panel reverts to its natural height the instant it
    // ends. Clearing `open` in the same frame is what stops that showing as a flash of the full panel.
    animation.onfinish = function () {
      panel.slideAnimation = null;

      if (done) done();

      panel.style.overflow = '';
      panel.style.height = '';
    };

    // A cancel means another click arrived mid-slide; that click owns the panel now.
    animation.oncancel = function () {
      panel.slideAnimation = null;
    };
  }

  function open(panel) {
    panel.open = true;

    // Measured with the height released, so it is the natural full height rather than whatever a cancelled
    // animation left inline.
    panel.style.height = '';

    slide(panel, panel.getBoundingClientRect().height);
  }

  function close(panel) {
    var bar = barOf(panel);
    var collapsed = bar ? bar.getBoundingClientRect().height : 0;

    // `open` is cleared at the end, not the start: the content has to stay rendered for the whole slide.
    slide(panel, collapsed, function () {
      panel.open = false;
    });
  }

  // The group the section asked for, saved before the attribute is removed. Panels with no group close nothing.
  function siblings(panel) {
    var group = panel.getAttribute('data-accordion-group');
    var section = panel.closest('[data-accordion-list]');

    if (!group || !section) {
      return [];
    }

    return panelsIn(section).filter(function (other) {
      return other !== panel && other.open && other.getAttribute('data-accordion-group') === group;
    });
  }

  function onClick(event) {
    var bar = event.target.closest && event.target.closest('.accordion-list__bar');

    if (!bar) {
      return;
    }

    var panel = bar.parentElement;

    if (!panel || !panel.classList.contains('accordion-list__panel')) {
      return;
    }

    // A link inside the bar, if one is ever added, must stay a link.
    if (event.target.closest('a')) {
      return;
    }

    // The toggle is taken over entirely: the browser would flip `open` immediately and there would be nothing
    // left to animate.
    event.preventDefault();

    if (panel.open) {
      close(panel);
      return;
    }

    siblings(panel).forEach(close);
    open(panel);
  }

  /**
   * Bound the first time a section is actually found, and delegated from the document from then on -- so it
   * covers every panel on the page, including ones the theme editor adds later, without a listener per panel.
   */
  function bind() {
    if (bound) {
      return;
    }

    bound = true;
    document.addEventListener('click', onClick);
  }

  /**
   * Lifts the native exclusivity so it can be animated instead -- see the note at the top. The attribute is only
   * removed once the handler above is in place to replace it.
   */
  function init(root) {
    var panels = panelsIn(root);

    if (!panels.length) {
      return;
    }

    bind();

    panels.forEach(function (panel) {
      var group = panel.getAttribute('name');

      if (group) {
        panel.setAttribute('data-accordion-group', group);
        panel.removeAttribute('name');
      }
    });
  }

  init(document);

  // Editor only. These events never fire on the storefront, so there is nothing to subscribe to there.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });

    // Selecting a panel block opens it, so the merchant sees what they are editing.
    document.addEventListener('shopify:block:select', function (event) {
      var panel = event.target.closest ? event.target.closest('.accordion-list__panel') : null;

      if (panel && !panel.open) {
        siblings(panel).forEach(close);
        open(panel);
      }
    });
  }
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * MULTI-COLUMN IMAGE FLIP
 * ------------------------------------------------------------------------------------------------------------
 *
 * Tap to flip on touch devices. Hover is handled in CSS; this only covers pointers that cannot hover.
 *
 * Nothing is bound until a flipping column is found, so a page without one pays a single querySelector at parse
 * and registers no listeners. The theme editor event is only subscribed to in the editor.
 *
 * See sections/multi-column.liquid and the "Multi-column image flip" block in custom.css.
 */
(function () {
  'use strict';

  var FLIP_SELECTOR = '.multi-column__image-wrapper--flip';
  var bound = false;

  function onClick(event) {
    if (window.matchMedia('(hover: hover)').matches) {
      return;
    }

    var wrapper = event.target.closest ? event.target.closest(FLIP_SELECTOR) : null;

    // Tapping anywhere else flips every open card back.
    document.querySelectorAll(FLIP_SELECTOR + '.is-flipped').forEach(function (flipped) {
      if (flipped !== wrapper) {
        flipped.classList.remove('is-flipped');
      }
    });

    if (!wrapper) {
      return;
    }

    // On a linked column the first tap flips, the second one follows the link.
    if (wrapper.tagName === 'A' && !wrapper.classList.contains('is-flipped')) {
      event.preventDefault();
    }

    wrapper.classList.toggle('is-flipped');
  }

  // Delegated from the document once bound, so it covers every column on the page with one listener.
  function init(root) {
    if (bound || !root.querySelector(FLIP_SELECTOR)) {
      return;
    }

    bound = true;
    document.addEventListener('click', onClick);
  }

  init(document);

  // Editor only: a merchant can turn the toggle on after load, so the section has to be re-checked.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * MEGA MENU EQUAL HEIGHTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Every dropdown opens at the height of the tallest one, so the panel does not resize as the pointer moves
 * along the bar. The tallest is measured rather than hard-coded: the panels are built from the navigation menu
 * in the admin, so their content changes without the theme changing.
 *
 * Measuring works while the panels are closed -- Focal hides them with visibility/opacity, not display, so they
 * are laid out at all times. Heights are read with the cap and the previous floor lifted, then the largest is
 * written back to --mega-menu-min-height (see "Mega menu" in custom.css).
 */
(function () {
  'use strict';

  var SELECTOR = '.mega-menu__inner';
  var frame = null;

  function measure() {
    frame = null;

    var panels = document.querySelectorAll(SELECTOR);

    if (panels.length < 2) {
      return;
    }

    // The panel's own max-height keeps a long menu inside the viewport; the floor must not push past it.
    var cap = parseFloat(window.getComputedStyle(panels[0]).maxHeight) || Infinity;

    document.documentElement.style.removeProperty('--mega-menu-min-height');

    panels.forEach(function (panel) {
      panel.style.maxHeight = 'none';
    });

    var tallest = 0;

    panels.forEach(function (panel) {
      tallest = Math.max(tallest, panel.offsetHeight);
    });

    panels.forEach(function (panel) {
      panel.style.removeProperty('max-height');
    });

    // Zero means the navigation is below its breakpoint and collapsed to the mobile drawer.
    if (tallest > 0) {
      document.documentElement.style.setProperty('--mega-menu-min-height', Math.min(tallest, cap) + 'px');
    }
  }

  function schedule() {
    if (frame === null) {
      frame = window.requestAnimationFrame(measure);
    }
  }

  schedule();

  // Images and webfonts both land after first paint and both change the tally.
  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(schedule);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', schedule);
  }
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * FEATURE CARD CAROUSEL
 * ------------------------------------------------------------------------------------------------------------
 *
 * The crossfading image stack in the media half of the "Feature card" section. The section's own image is the
 * first slide and every image block adds one after it; a card with a single image is not a carousel and nothing
 * below ever touches it.
 *
 * The fade itself is CSS -- the slides are stacked in one box and only .is-active is opaque -- so all this does
 * is move that class, and no layout is ever recalculated.
 *
 * Autoplay stops whenever it would be either rude or wasted: while the card is hovered or holds focus, while the
 * tab is in the background, while the card is off screen, and entirely for a visitor who has asked for reduced
 * motion. The arrows and swiping still work in every one of those cases.
 *
 * COST OFF THE PAGES THAT USE IT. Nothing is bound and no timer is started unless a carousel is on the page.
 *
 * See sections/feature-card.liquid and snippets/feature-card-slide.liquid for the markup, and the "Image
 * carousel" block in custom.css.
 */
(function () {
  'use strict';

  var carousels = [];
  var watchingTab = false;

  function slidesOf(carousel) {
    return Array.prototype.slice.call(carousel.querySelectorAll('[data-feature-card-slide]'));
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function show(state, index) {
    var count = state.slides.length;

    // Wrapped rather than clamped: the arrows are always live, so running off either end is a deliberate loop.
    state.index = (index % count + count) % count;

    state.slides.forEach(function (slide, i) {
      var isActive = i === state.index;

      slide.classList.toggle('is-active', isActive);

      // The stack is only hidden by opacity, so the inactive pictures would otherwise all be read out.
      if (isActive) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function step(state, forwards) {
    show(state, state.index + (forwards ? 1 : -1));

    // A manual move restarts the countdown, so the slide just asked for gets its full turn on screen.
    start(state);
  }

  function stop(state) {
    if (state.timer) {
      window.clearInterval(state.timer);
      state.timer = null;
    }
  }

  /**
   * Runs only when every condition for autoplay holds. Called again after anything that could have changed one of
   * them, so this is the single place the timer is ever created.
   */
  function start(state) {
    stop(state);

    if (!state.autoplay || state.paused || state.hidden || reducedMotion()) {
      return;
    }

    state.timer = window.setInterval(function () {
      show(state, state.index + 1);
    }, state.interval);
  }

  function pause(state, paused) {
    state.paused = paused;
    start(state);
  }

  function bind(carousel) {
    var slides = slidesOf(carousel);

    if (slides.length < 2) {
      return;
    }

    var interval = parseInt(carousel.getAttribute('data-interval'), 10);

    var state = {
      carousel: carousel,
      slides: slides,
      index: 0,
      timer: null,
      paused: false,
      hidden: false,
      autoplay: carousel.getAttribute('data-autoplay') !== 'false',
      interval: isNaN(interval) ? 5000 : Math.max(1000, interval)
    };

    carousel.addEventListener('click', function (event) {
      var arrow = event.target.closest('[data-feature-card-prev], [data-feature-card-next]');

      if (arrow) {
        step(state, arrow.hasAttribute('data-feature-card-next'));
      }
    });

    // Hover and focus are the same pause: a visitor reading the picture, or tabbing the arrows, should not have it
    // change under them.
    //
    // Only on a pointer that can really hover. A tap on a touch screen is followed by a synthetic mouseenter with
    // no mouseleave after it, which would leave the card paused for good.
    carousel.addEventListener('mouseenter', function () {
      if (window.matchMedia('(hover: hover)').matches) {
        pause(state, true);
      }
    });

    carousel.addEventListener('mouseleave', function () {
      if (window.matchMedia('(hover: hover)').matches) {
        pause(state, false);
      }
    });

    carousel.addEventListener('focusin', function () { pause(state, true); });
    carousel.addEventListener('focusout', function () { pause(state, false); });

    // Arrow keys while an arrow has focus, which is what a keyboard visitor who has just reached them expects.
    carousel.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      if (!event.target.closest('[data-feature-card-prev], [data-feature-card-next]')) {
        return;
      }

      // Arrow keys are physical, so the two horizontal ones swap over in a right-to-left locale.
      var isRtl = document.documentElement.getAttribute('dir') === 'rtl';

      event.preventDefault();
      step(state, event.key === 'ArrowRight' ? !isRtl : isRtl);
    });

    bindSwipe(state);

    // Off screen there is nothing to see, so the timer is not worth running. Without support the card simply
    // cycles the whole time, which is what it did before this check existed.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        state.hidden = !entries[0].isIntersecting;
        start(state);
      }, { threshold: 0.2 }).observe(carousel);
    }

    show(state, 0);
    start(state);
    carousels.push(state);
  }

  /**
   * Touch only. The slides do not move under the finger -- there is nothing to drag, since the transition is a
   * fade -- so the gesture is read on release and turned into a single step.
   */
  function bindSwipe(state) {
    var origin = null;

    state.carousel.addEventListener('touchstart', function (event) {
      origin = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      pause(state, true);
    }, { passive: true });

    state.carousel.addEventListener('touchend', function (event) {
      var from = origin;

      origin = null;
      pause(state, false);

      if (!from || !event.changedTouches.length) {
        return;
      }

      var dx = event.changedTouches[0].clientX - from.x;
      var dy = event.changedTouches[0].clientY - from.y;

      // Has to read clearly as sideways, so a diagonal flick while scrolling the page is left alone.
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) {
        return;
      }

      var isRtl = document.documentElement.getAttribute('dir') === 'rtl';

      step(state, isRtl ? dx > 0 : dx < 0);
    }, { passive: true });
  }

  function init(root) {
    var found = root.querySelectorAll('[data-feature-card-carousel]');

    if (!found.length) {
      return;
    }

    // One listener for the page, and only once a carousel is actually present: a backgrounded tab throttles the
    // timer to a crawl anyway, and the slides would all have changed by the time anyone looked again.
    if (!watchingTab) {
      watchingTab = true;

      document.addEventListener('visibilitychange', function () {
        carousels.forEach(function (state) {
          state.hidden = document.hidden;
          start(state);
        });
      });
    }

    found.forEach(bind);
  }

  init(document);

  // The theme editor replaces the whole section on every edit, which leaves the old card's timer running against a
  // detached node -- so the state for anything no longer in the document is dropped along with its timer.
  document.addEventListener('shopify:section:load', function (event) {
    carousels = carousels.filter(function (state) {
      if (state.carousel.isConnected) {
        return true;
      }

      stop(state);
      return false;
    });

    init(event.target);
  });
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * SLIDESHOW PARALLAX
 * ------------------------------------------------------------------------------------------------------------
 *
 * Drifts the sky-and-clouds plate behind the terminal cut-out in a layered hero slide, so the clouds appear to
 * pass behind the roofline as the section scrolls away. The cut-out never moves.
 *
 * Only wrappers that actually carry a foreground image drift: a breakpoint still using a flat composite would
 * otherwise take the building along with the sky.
 *
 * The heading can travel too, in the opposite direction -- it is the nearest plane, so it moves fastest. A hero
 * sits at the top of the page and only ever scrolls out, so it has about half the travel window a mid-page
 * section gets; opposing the two planes widens the gap between them without needing more scroll to do it.
 *
 * The travel span exists because the sky is at the TOP of the hero and is therefore the first part to leave the
 * viewport. Spread over the whole exit, half the drift is spent moving clouds that are already off screen.
 * Finishing early concentrates it into the stretch where there is still sky to see it in; the offset is clamped
 * either way, so the plate never travels further than its overhang allows.
 *
 * COST OFF THE PAGES THAT USE IT. One querySelectorAll at parse; no listener is bound unless a hero with a
 * strength and a layered wrapper is present.
 *
 * See sections/slideshow.liquid for the markup and "Slideshow hero parallax" in custom.css for the geometry.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 740.98px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  var heroes = [];
  var frame = null;
  var scrolling = false;

  // The fraction of the section's exit the drift is spread over. Falls back to all of it.
  function spanOf(hero, attribute) {
    var value = parseFloat(hero.getAttribute(attribute));

    return value > 0 ? value : 1;
  }

  function strengthOf(hero, attribute) {
    var value = parseFloat(hero.getAttribute(attribute));

    return value > 0 ? value : 0;
  }

  function collect() {
    heroes = [];

    Array.prototype.forEach.call(document.querySelectorAll('.slideshow--parallax'), function (hero) {
      var strength = strengthOf(hero, 'data-parallax');
      var textStrength = strengthOf(hero, 'data-parallax-text');

      var layers = strength ? Array.prototype.slice.call(
        hero.querySelectorAll('.slideshow__image-wrapper--layered .slideshow__image')) : [];
      var texts = textStrength ? Array.prototype.slice.call(
        hero.querySelectorAll('.slideshow__text-wrapper')) : [];

      if (layers.length || texts.length) {
        heroes.push({
          element: hero,
          strength: strength,
          textStrength: textStrength,
          span: spanOf(hero, 'data-parallax-span'),
          spanMobile: spanOf(hero, 'data-parallax-span-mobile'),
          layers: layers,
          texts: texts
        });
      }
    });
  }

  function draw() {
    if (REDUCED.matches) {
      return;
    }

    heroes.forEach(function (hero) {
      var rect = hero.element.getBoundingClientRect();

      if (!rect.height || rect.bottom < 0) {
        return;
      }

      // 0 while the hero is at the top of the page, 1 once it has scrolled past the span. Clamped at both ends,
      // so a hero pushed down by an announcement bar rests at 0 and the drift holds once the span is spent.
      var span = MOBILE.matches ? hero.spanMobile : hero.span;
      var progress = Math.min(1, Math.max(0, -rect.top / (rect.height * span)));

      // Positive, so the plate travels down inside the frame while the page carries the frame up -- it covers
      // less ground than the scroll does, which is what reads as distance.
      var offset = (progress * rect.height * hero.strength).toFixed(2);

      hero.layers.forEach(function (layer) {
        layer.style.transform = 'translate3d(0, ' + offset + 'px, 0)';
      });

      // Negative: the heading is the nearest plane, so it outruns the scroll rather than lagging behind it.
      var textOffset = (-progress * rect.height * hero.textStrength).toFixed(2);

      hero.texts.forEach(function (text) {
        text.style.transform = 'translate3d(0, ' + textOffset + 'px, 0)';
      });
    });
  }

  function schedule() {
    if (frame) {
      return;
    }

    frame = requestAnimationFrame(function () {
      frame = null;
      draw();
    });
  }

  function listen() {
    var drifts = heroes.length > 0 && !REDUCED.matches;

    if (drifts && !scrolling) {
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      scrolling = true;
    } else if (!drifts && scrolling) {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      scrolling = false;
    }
  }

  function refresh() {
    collect();
    listen();
    draw();
  }

  refresh();

  // A reload part-way down the page restores the scroll position without firing a scroll event.
  window.addEventListener('load', draw);

  // Editor only: the section is re-rendered on every edit, which replaces the images this holds.
  if (window.Shopify && window.Shopify.designMode) {
    ['shopify:section:load', 'shopify:section:unload'].forEach(function (name) {
      document.addEventListener(name, refresh);
    });
  }
}());

/**
 * ------------------------------------------------------------------------------------------------------------
 * AIRLINE FLIGHTS
 * ------------------------------------------------------------------------------------------------------------
 */
(function () {
  'use strict';

  function select(tab) {
    var root = tab.closest('.airline-flights');

    if (!root) {
      return;
    }

    var tabs = Array.prototype.slice.call(root.querySelectorAll('.airline-flights__tab'));
    var boards = Array.prototype.slice.call(root.querySelectorAll('.airline-flights__board'));
    var panel = root.querySelector('.airline-flights__boards');
    var views = (tab.getAttribute('data-flight-views') || '').split(' ');

    tabs.forEach(function (candidate) {
      var isSelected = candidate === tab;

      candidate.classList.toggle('is-active', isSelected);
      candidate.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      candidate.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    boards.forEach(function (board) {
      board.hidden = views.indexOf(board.getAttribute('data-flight-view')) === -1;
    });

    if (panel && tab.id) {
      panel.setAttribute('aria-labelledby', tab.id);
    }
  }

  document.addEventListener('click', function (event) {
    var tab = event.target.closest('.airline-flights__tab');

    if (tab) {
      select(tab);
    }
  });

  document.addEventListener('keydown', function (event) {
    var tab = event.target.closest('.airline-flights__tab');

    if (!tab) {
      return;
    }

    var tabs = Array.prototype.slice.call(tab.closest('[role="tablist"]').querySelectorAll('.airline-flights__tab'));
    var index = tabs.indexOf(tab);
    var next = null;
    var isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + (isRtl ? -1 : 1) + tabs.length) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index + (isRtl ? 1 : -1) + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    select(next);
    next.focus();
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (event) {
      var tab = event.target.closest ? event.target.closest('.airline-flights__tab') : null;

      if (tab) {
        select(tab);
      }
    });
  }
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * VIDEO GRID POPUP
 * ------------------------------------------------------------------------------------------------------------
 *
 * Watch copies the card's video (Shopify, YouTube or Vimeo) into the section's <dialog>; closing empties it,
 * which stops playback. See sections/video-grid.liquid.
 */
(function () {
  'use strict';

  var bound = false;

  // Pauses the card's own player so the same video isn't playing twice.
  function pauseInline(item) {
    item.querySelectorAll('.video-grid__thumbnail video, .video-grid__thumbnail iframe').forEach(function (media) {
      if (media.tagName === 'VIDEO') {
        media.pause();
      } else if (media.contentWindow) {
        var message = media.src.indexOf('youtube') !== -1 ? { event: 'command', func: 'pauseVideo', args: '' } : { method: 'pause' };
        media.contentWindow.postMessage(JSON.stringify(message), '*');
      }
    });
  }

  function onClick(event) {
    var opener = event.target.closest('[data-video-grid-open]');

    if (opener) {
      var item = opener.closest('.video-grid__item');
      var dialog = opener.closest('.video-grid').querySelector('.video-grid__dialog');
      var template = item.querySelector('template[data-video-grid-popup]');

      if (!dialog || !template || typeof dialog.showModal !== 'function') {
        return;
      }

      pauseInline(item);
      dialog.querySelector('.video-grid__player').replaceChildren(template.content.cloneNode(true));
      dialog.showModal();
      document.documentElement.classList.add('lock-all');
      return;
    }

    // Close button, or a click on the backdrop (which targets the dialog itself).
    var target = event.target.closest('[data-video-grid-close]') || event.target;

    if (target.matches('[data-video-grid-close]') || target.matches('.video-grid__dialog')) {
      target.closest('.video-grid__dialog').close();
    }
  }

  // Fires for Esc too.
  function onClose(event) {
    if (event.target.matches && event.target.matches('.video-grid__dialog')) {
      event.target.querySelector('.video-grid__player').replaceChildren();
      document.documentElement.classList.remove('lock-all');
    }
  }

  // Nothing is bound until a video grid is on the page.
  function init(root) {
    if (!root.querySelector('.video-grid')) {
      return;
    }

    if (bound) {
      return;
    }

    bound = true;
    document.addEventListener('click', onClick);
    document.addEventListener('close', onClose, true);
  }

  init(document);

  // Editor only: the section can be added after load.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();
